import { FC, useEffect, useMemo } from 'react';
import { Provider } from 'react-redux';
import { useRoutes } from 'react-router-dom';
import { ThemeProvider, createTheme, useTheme } from '@mui/material';
// @ts-ignore
import withAuthGuard from 'digital_health_authenticator/Auth';
import Cookies from 'js-cookie';
import createRoutes from '~/Router';
import { InitLoadingSpinner } from '~/components';
import { ENV } from '~/config';
import { withTestGuard } from '~/pages';
import { AppProps } from '~/pages/Auth/types';
import store, { useAppSelector } from '~/redux';
import { setAuthTokens } from '~/redux/slices/authSlice';
import { selectIsSessionInitialized, setUserSession } from '~/redux/slices/userSessionSlice';
import unlockTheme from './theme';

const AppComponent: FC<AppProps> = ({ user, signOut, userSession }) => {
  const theme = useTheme();
  const isSessionInitialized = useAppSelector(selectIsSessionInitialized);
  const accessToken = userSession.accessToken.toString(),
    idToken = userSession.idToken.toString();

  const parsedUserSession = useMemo(
    () => ({
      idToken: idToken,
      accessToken: accessToken,
    }),
    [accessToken, idToken],
  );

  useEffect(() => {
    store.dispatch(setUserSession({ ...parsedUserSession, user, isSessionInitialized: true }));
    store.dispatch(
      setAuthTokens({
        accessToken: parsedUserSession.accessToken,
        idToken: parsedUserSession.idToken,
      }),
    );
  }, [user, parsedUserSession]);

  const routesElement = useRoutes(createRoutes(signOut));

  return (
    <Provider store={store}>
      <ThemeProvider theme={createTheme(theme, { ...unlockTheme })}>
        {isSessionInitialized ? routesElement : <InitLoadingSpinner />}
      </ThemeProvider>
    </Provider>
  );
};

const AuthApp = withAuthGuard(AppComponent, {
  loginProps: {
    appName: 'Healthcare Data Platform',
    createAcntLink:
      'https://medtronicprod.service-now.com/it?id=sc_cat_item&sys_id=8c856e58c3b58e107ec5e7deb00131fb&table=sc_cat_item&searchTerm=healthcare%20data%20platform/',
    helpLink: 'mailto:rs.digitalhealth-healthcaredata@medtronic.com',
    caption: 'This website is intended for authorized users.',
  },
  awsConfigProps: ENV.VITE_AUTH_CONFIG,
});

const TestApp = withTestGuard(AppComponent);

export default function App() {
  const isTestMode = Cookies.get('testMode') === 'true';

  return (
    <>
      {isTestMode ? (
        <Provider store={store}>
          <TestApp />
        </Provider>
      ) : (
        <Provider store={store}>
          <AuthApp />
        </Provider>
      )}
    </>
  );
}
